import React from 'react';
import { cn } from '../../../lib/utils';

// Base skeleton component
export const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={cn(
      'animate-pulse rounded bg-gray-200 dark:bg-gray-700',
      className,
    )}
  />
);

// Dashboard skeleton for full dashboard loading
export const DashboardSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div
    className={cn(
      'bg-gray-50/30 p-6 font-sans antialiased dark:bg-gray-800/30',
      className,
    )}
  >
    {/* Dashboard Sections */}
    <div className="space-y-10">
      {Array.from({ length: 2 }).map((_, sectionIndex) => (
        <div key={sectionIndex}>
          {/* Section Header */}
          <div className="mb-6">
            <Skeleton className="mb-2 h-8 w-64" />
            <Skeleton className="h-5 w-96" />
          </div>

          {/* Widget Grid */}
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, widgetIndex) => (
              <WidgetSkeleton key={widgetIndex} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Widget skeleton for individual widgets
export const WidgetSkeleton: React.FC<{
  className?: string;
  variant?: 'metric' | 'table' | 'chart' | 'status' | 'progress' | 'category';
}> = ({ className, variant = 'metric' }) => {
  const renderSkeletonContent = () => {
    switch (variant) {
      case 'metric':
        return <MetricCardSkeleton />;
      case 'table':
        return <DataTableSkeleton />;
      case 'chart':
        return <ChartSkeleton />;
      case 'status':
        return <StatusCardSkeleton />;
      case 'progress':
        return <ProgressCardSkeleton />;
      case 'category':
        return <CategoryCardSkeleton />;
      default:
        return <MetricCardSkeleton />;
    }
  };

  return (
    <div
      className={cn(
        'h-full min-h-[200px] w-full rounded-lg border bg-white p-6 shadow-sm dark:bg-gray-900',
        className,
      )}
    >
      {renderSkeletonContent()}
    </div>
  );
};

// Metric card skeleton
export const MetricCardSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn('flex h-full flex-col', className)}>
    <div className="flex items-center gap-2">
      <Skeleton className="h-4 w-4" />
      <Skeleton className="h-4 w-24" />
    </div>
    <div className="mt-4 flex-1">
      <Skeleton className="mb-2 h-8 w-32" />
      <Skeleton className="h-4 w-20" />
    </div>
    <div className="mt-4">
      <Skeleton className="h-3 w-16" />
    </div>
  </div>
);

// Data table skeleton
export const DataTableSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn('h-full', className)}>
    <Skeleton className="mb-4 h-6 w-32" />
    <div className="space-y-3">
      <Skeleton className="h-8 w-full" />
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton key={index} className="h-6 w-full" />
      ))}
    </div>
  </div>
);

// Chart skeleton
export const ChartSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn('h-full', className)}>
    <Skeleton className="mb-4 h-6 w-32" />
    <Skeleton className="h-48 w-full" />
    <div className="mt-2 flex justify-center gap-4">
      <Skeleton className="h-3 w-12" />
      <Skeleton className="h-3 w-12" />
      <Skeleton className="h-3 w-12" />
    </div>
  </div>
);

// Status card skeleton
export const StatusCardSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn('flex h-full flex-col', className)}>
    <div className="flex items-center gap-3">
      <Skeleton className="h-6 w-6 rounded-full" />
      <Skeleton className="h-5 w-24" />
    </div>
    <div className="mt-4 flex-1">
      <Skeleton className="mb-2 h-6 w-full" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  </div>
);

// Progress card skeleton
export const ProgressCardSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn('flex h-full flex-col', className)}>
    <div className="flex items-center gap-2">
      <Skeleton className="h-4 w-4" />
      <Skeleton className="h-5 w-24" />
    </div>
    <div className="mt-4 flex-1">
      <Skeleton className="mb-2 h-2 w-full" />
      <Skeleton className="h-4 w-16" />
    </div>
  </div>
);

// Category card skeleton
export const CategoryCardSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn('h-full', className)}>
    <Skeleton className="mb-4 h-6 w-24" />
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded" />
          <div className="flex-1">
            <Skeleton className="mb-1 h-4 w-16" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      ))}
    </div>
  </div>
);
